import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Sparkles, FileUser, Link2, Zap, Clock, TrendingUp } from 'lucide-react';
import UnifiedHeader from '../../components/unified/UnifiedHeader';
import { UnifiedInput, PageTransition } from '../../components/unified';
import Chip from '../../components/ui/Chip';
import { typography, colors, icons, shadows, motion as motionTokens } from '../../styles/design-tokens';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { generateSearchSuggestions } from '../../services/semanticSearchService';
import { getSearchHistory, addToSearchHistory } from '../../services/searchHistoryService';

const HomePage = () => {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);

  // Load recent searches on mount
  useEffect(() => {
    const history = getSearchHistory(5);
    setRecentSearches(history?.map(h => h?.query));
  }, []);

  // Generate suggestions as user types
  useEffect(() => {
    const generateSuggestions = async () => {
      if (searchQuery?.length >= 2) {
        try {
          const results = await generateSearchSuggestions(searchQuery, recentSearches);
          setSuggestions(results);
          setShowSuggestions(true);
        } catch (error) {
          setSuggestions(recentSearches?.slice(0, 5));
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const debounceTimeout = setTimeout(generateSuggestions, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchQuery, recentSearches]);

  const handleSearch = (query) => {
    const searchTerm = query || searchQuery;
    if (searchTerm?.trim()) {
      // Add to search history
      addToSearchHistory(searchTerm?.trim());
      
      // Navigate to search results
      navigate(`/jobs?q=${encodeURIComponent(searchTerm?.trim())}`);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter') {
      handleSearch();
    }
  };

  const exampleChips = [
    { text: "Marketing jobs in Toronto", variant: "accent" },
    { text: "Remote developer roles", variant: "success" }, 
    { text: "HR manager in Montréal", variant: "neutral" }
  ];

  const features = [
    {
      icon: FileUser,
      title: "Profiles, not PDFs",
      description: "Resume optional → AI builds structured profiles."
    },
    {
      icon: Link2,
      title: "Paste URL to onboard",
      description: "AI creates Company Page instantly."
    },
    {
      icon: Zap,
      title: "AI-powered ATS",
      description: "Ranking, summaries, and pipeline automation."
    }
  ];

  // Use design token variants with reduce-motion support
  const containerVariants = prefersReducedMotion 
    ? motionTokens?.reducedMotion?.variants?.static 
    : motionTokens?.variants?.staggerContainer;
  
  const itemVariants = prefersReducedMotion 
    ? motionTokens?.reducedMotion?.variants?.static 
    : motionTokens?.variants?.staggerItem;

  return (
    <PageTransition>
      <div className="min-h-screen bg-background flex flex-col">
        {/* Header */}
        <UnifiedHeader variant="public" onToggleSidebar={() => {}} />
        
        {/* Hero Section */}
        <motion.main 
          className="flex-1 flex flex-col items-center justify-center px-6 pb-20 pt-24"
          variants={containerVariants}
          initial="initial"
          animate="animate"
        >
          <div className="w-full max-w-2xl mx-auto text-center flex flex-col gap-8">
            {/* Wordmark */}
            <motion.h1 
              className={`${typography?.display?.large} ${colors?.text?.primary}`}
              variants={itemVariants}
            >
              Recrutees
            </motion.h1>

            {/* Tagline */}
            <motion.p 
              className={`${typography?.body?.large} ${colors?.text?.secondary}`}
              variants={itemVariants}
            >
              AI-powered job search and ATS that just works.
            </motion.p>

            {/* Search Bar */}
            <motion.div 
              className="relative"
              variants={itemVariants}
            >
              <div className="relative group">
                <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none z-10">
                  <Sparkles className={`${icons?.md} text-primary`} />
                </div>
                <UnifiedInput
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  onKeyPress={handleKeyPress}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder="Ask me anything… e.g. 'remote designer jobs in Toronto'"
                  className={`h-14 pl-12 pr-4 rounded-full shadow-card ${!prefersReducedMotion ? 'hover:shadow-elevated transition-shadow duration-200' : ''}`}
                />
              </div>

              {/* AI Search Suggestions */}
              {showSuggestions && suggestions?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`absolute z-20 w-full mt-2 ${colors?.background?.card} rounded-lg shadow-elevated border ${colors?.border?.default} overflow-hidden`}
                >
                  <div className={`p-2 ${colors?.background?.secondary} border-b ${colors?.border?.default}`}>
                    <div className="flex items-center gap-2">
                      <Sparkles className={`w-4 h-4 text-primary`} />
                      <span className={`${typography?.caption} font-medium ${colors?.text?.primary}`}>
                        AI Suggestions
                      </span>
                    </div>
                  </div>
                  {suggestions?.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleSearch(suggestion)}
                      className={`w-full text-left px-4 py-3 ${colors?.text?.primary} ${typography?.body?.small} hover:${colors?.background?.hover} transition-colors flex items-center gap-3`}
                    >
                      {recentSearches?.includes(suggestion) ? (
                        <Clock className="w-4 h-4 text-muted" />
                      ) : (
                        <TrendingUp className="w-4 h-4 text-accent" />
                      )}
                      <span>{suggestion}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </motion.div>

            {/* Example Chips */}
            <motion.div 
              className="flex flex-wrap items-center justify-center gap-2"
              variants={itemVariants}
            >
              {exampleChips?.map((chip, index) => (
                <motion.div
                  key={index}
                  whileHover={!prefersReducedMotion ? motionTokens?.hover?.scaleSubtle : {}}
                  whileTap={!prefersReducedMotion ? motionTokens?.tap?.scaleSubtle : {}}
                  transition={{ duration: motionTokens?.duration?.fast }}
                >
                  <Chip
                    variant={chip?.variant}
                    size="medium"
                    interactive
                    onClick={() => handleSearch(chip?.text)}
                  >
                    {chip?.text}
                  </Chip>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Feature Cards */}
          <motion.div 
            className="grid md:grid-cols-3 gap-6 mt-16"
            variants={containerVariants}
          >
            {features?.map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={!prefersReducedMotion ? motionTokens?.hover?.liftSubtle : {}}
                transition={{ duration: motionTokens?.duration?.fast, ease: motionTokens?.easing?.default }}
                className={`bg-card p-6 rounded-xl border border-border ${!prefersReducedMotion ? shadows?.hover?.card : shadows?.card}`}
              >
                <div className={`${colors?.background?.primary} p-3 rounded-lg inline-block mb-4`}>
                  <feature.icon className={`${icons?.lg} text-primary`} />
                </div>
                <h3 className={`${typography?.h3} mb-2`}>{feature?.title}</h3>
                <p className={colors?.text?.secondary}>{feature?.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.main>
        
        {/* Footer */}
        <motion.footer 
          className="w-full py-8 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: prefersReducedMotion ? 0 : 0.8, duration: prefersReducedMotion ? 0.01 : 0.6 }}
        >
          <p className={`${typography?.body?.small} ${colors?.text?.secondary}`}>
            © 2025 Recrutees · Privacy · Terms
          </p>
        </motion.footer>
      </div>
    </PageTransition>
  );
};

export default HomePage;